/**
 * WebchatPreview - Preview interativo do widget de webchat
 * 
 * Renderiza o widget REAL (não mockado) dentro do modal,
 * permitindo testar conversas e validar fluxos vinculados.
 * 
 * @version 2.0.0 - Preview interativo com widget real
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Toggle } from '@/components/ui/toggle';
import { Monitor, Smartphone } from 'lucide-react';
import { WidgetPreviewHost } from './WidgetPreviewHost';

// Interface da configuração recebida do WebchatConfigDialog
interface PreviewConfig {
  inbox_id: string;
  primary_color: string;
  secondary_color: string;
  text_color: string;
  position: 'bottom-right' | 'bottom-left';
  button_icon: string;
  greeting_message: string;
  placeholder_text: string;
  company_name: string;
  company_logo_url: string | null;
  require_name: boolean;
  require_email: boolean;
  show_branding: boolean;
}

interface WebchatPreviewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  config: PreviewConfig;
}

export function WebchatPreview({ open, onOpenChange, config }: WebchatPreviewProps) {
  const [isMobile, setIsMobile] = useState(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[95vh] max-h-[900px] flex flex-col overflow-hidden">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle>Preview do Widget</DialogTitle>
              <DialogDescription>
                Teste o widget em tempo real - envie mensagens e valide fluxos
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              <Toggle
                pressed={!isMobile}
                onPressedChange={() => setIsMobile(false)}
                aria-label="Desktop view"
              >
                <Monitor className="h-4 w-4" />
              </Toggle>
              <Toggle
                pressed={isMobile}
                onPressedChange={() => setIsMobile(true)}
                aria-label="Mobile view"
              >
                <Smartphone className="h-4 w-4" />
              </Toggle>
            </div>
          </div>
        </DialogHeader>

        {/* Área de preview */}
        <div className="flex-1 min-h-[650px] bg-muted/30 rounded-lg overflow-auto relative mt-4">
          {/* Simulação de página de fundo */}
          <div className="absolute inset-0 p-4 bg-gradient-to-br from-background to-muted/50">
            <div className="w-full h-full border-2 border-dashed border-muted-foreground/20 rounded-lg flex items-center justify-center">
              <span className="text-muted-foreground/50">Seu website aqui</span>
            </div>
          </div>

          {/* Container do widget real */}
          <div
            className={`absolute z-10 transition-all duration-300 ${
              config.position === 'bottom-right' ? 'right-4' : 'left-4'
            } bottom-4`}
            style={{
              maxWidth: isMobile ? 'calc(100% - 32px)' : '380px',
              width: isMobile ? 'calc(100% - 32px)' : '380px',
              height: 'calc(100% - 32px)',
            }}
          >
            {/* Widget real interativo */}
            <WidgetPreviewHost 
              inboxId={config.inbox_id}
              position={config.position}
              isMobile={isMobile}
            />
          </div>
        </div>

        {/* Botões */}
        <div className="flex justify-end mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
